// src/pages/auth/LoginPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import { login as apiLogin } from '../../services/api';

const EyeIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
  </svg>
);
const EyeOffIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
    <line x1="1" y1="1" x2="23" y2="23"/>
  </svg>
);

export default function LoginPage() {
  const [form, setForm] = useState({ username: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.username || !form.password) {
      toast.warning('กรุณากรอกชื่อผู้ใช้และรหัสผ่าน');
      return;
    }
    setLoading(true);
    try {
      const res = await apiLogin(form);
      if (res.data.success) {
        login(res.data.token, res.data.user);
        toast.success('ยินดีต้อนรับ ' + res.data.user.firstname + ' ' + res.data.user.lastname);
        const returnTo = sessionStorage.getItem('wadphol_return_to');
        sessionStorage.removeItem('wadphol_return_to');
        navigate(returnTo && returnTo !== '/login' ? returnTo : '/');
      } else {
        toast.error(res.data.message);
      }
    } catch (err) {
      const isTimeout = err?.code === 'ECONNABORTED' || err?.code === 'ETIMEDOUT';
      const msg = err.response?.data?.message
        || (isTimeout ? 'เซิร์ฟเวอร์ไม่ตอบสนอง กรุณาลองใหม่ หรือติดต่อผู้ดูแลระบบ' : null)
        || (err.response ? 'เข้าสู่ระบบไม่สำเร็จ' : 'ไม่สามารถเชื่อมต่อเซิร์ฟเวอร์ได้');
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = (extra) => ({
    width: '100%',
    border: '1.5px solid #e2e8f0',
    borderRadius: '12px',
    fontSize: '15px',
    fontFamily: "'Sarabun', sans-serif",
    background: 'white',
    color: '#1a1a2e',
    outline: 'none',
    transition: 'border-color 0.2s, box-shadow 0.2s',
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
    ...extra,
  });

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: isMobile ? 'column' : 'row', fontFamily: "'Sarabun', sans-serif" }}>

      {/* Left Panel */}
      <div style={{
        flex: isMobile ? 'none' : '1',
        background: 'linear-gradient(145deg, #0d47a1 0%, #1565C0 40%, #1976d2 70%, #1e88e5 100%)',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        padding: isMobile ? '36px 28px 32px' : '60px 48px',
        position: 'relative', overflow: 'hidden', minWidth: 0,
      }}>
        <div style={{ position:'absolute', top:'-80px', left:'-80px', width:'320px', height:'320px', borderRadius:'50%', background:'rgba(255,255,255,0.05)' }} />
        <div style={{ position:'absolute', bottom:'-60px', right:'-60px', width:'280px', height:'280px', borderRadius:'50%', background:'rgba(255,255,255,0.06)' }} />
        <div style={{ position:'absolute', top:'45%', right:'-40px', width:'160px', height:'160px', borderRadius:'50%', background:'rgba(255,255,255,0.04)' }} />

        <div style={{ position:'relative', zIndex:1, textAlign:'center', color:'white', maxWidth: isMobile ? '100%' : '380px' }}>
          <div style={{
            width: isMobile ? '72px' : '96px', height: isMobile ? '72px' : '96px',
            background:'rgba(255,255,255,0.15)', backdropFilter:'blur(10px)', borderRadius:'28px',
            display:'flex', alignItems:'center', justifyContent:'center',
            fontSize: isMobile ? '36px' : '48px', margin:'0 auto',
            marginBottom: isMobile ? '16px' : '32px',
            boxShadow:'0 8px 32px rgba(0,0,0,0.2)', border:'1.5px solid rgba(255,255,255,0.2)',
          }}>🎓</div>

          <h1 style={{ fontSize: isMobile ? '22px' : '30px', fontWeight:'800', marginBottom: isMobile ? '8px' : '12px', lineHeight:'1.2' }}>
            ระบบวัดผลการศึกษา
          </h1>
          <p style={{ fontSize: isMobile ? '14px' : '16px', opacity:0.85, lineHeight:'1.6', marginBottom: isMobile ? '0' : '40px' }}>
            บริหารจัดการผลการเรียน ตัวชี้วัด และการประเมินผลอย่างครบวงจร
          </p>

          {!isMobile && [
            { icon:'📊', text:'จัดการคะแนนและตัวชี้วัด' },
            { icon:'👨‍🏫', text:'ระบบครูและนักเรียน' },
            { icon:'📋', text:'รายงานผลการเรียน' },
          ].map((f, i) => (
            <div key={i} style={{
              display:'flex', alignItems:'center', gap:'12px',
              background:'rgba(255,255,255,0.1)', borderRadius:'12px',
              padding:'12px 18px', marginBottom:'10px', textAlign:'left',
              border:'1px solid rgba(255,255,255,0.12)',
            }}>
              <span style={{ fontSize:'20px' }}>{f.icon}</span>
              <span style={{ fontSize:'14px', fontWeight:'500' }}>{f.text}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Right Panel */}
      <div style={{
        width: isMobile ? '100%' : '480px', flexShrink: 0,
        background: isMobile ? 'white' : '#f8fafc',
        display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
        padding: isMobile ? '36px 24px 48px' : '48px',
      }}>
        <div style={{ width:'100%', maxWidth:'360px' }}>
          <div style={{ marginBottom: isMobile ? '28px' : '36px' }}>
            <h2 style={{ fontSize: isMobile ? '22px' : '26px', fontWeight:'800', color:'#1a1a2e', marginBottom:'6px' }}>เข้าสู่ระบบ</h2>
            <p style={{ fontSize:'14px', color:'#8896a4' }}>กรอกข้อมูลเพื่อเข้าใช้งานระบบ</p>
          </div>

          <form onSubmit={handleSubmit}>
            {/* Username */}
            <div style={{ marginBottom:'20px' }}>
              <label style={{ display:'block', fontSize:'13px', fontWeight:'600', color:'#374151', marginBottom:'8px' }}>ชื่อผู้ใช้งาน</label>
              <div style={{ position:'relative' }}>
                <span style={{ position:'absolute', left:'14px', top:'50%', transform:'translateY(-50%)', color:'#9ca3af', display:'flex' }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                  </svg>
                </span>
                <input type="text" placeholder="กรอกชื่อผู้ใช้" value={form.username}
                  onChange={e => setForm({...form, username: e.target.value})} autoFocus
                  style={inputStyle({ padding:'13px 14px 13px 42px' })}
                  onFocus={e => { e.target.style.borderColor='#1565C0'; e.target.style.boxShadow='0 0 0 3px rgba(21,101,192,0.12)'; }}
                  onBlur={e => { e.target.style.borderColor='#e2e8f0'; e.target.style.boxShadow='0 1px 3px rgba(0,0,0,0.04)'; }}
                />
              </div>
            </div>

            {/* Password */}
            <div style={{ marginBottom:'28px' }}>
              <label style={{ display:'block', fontSize:'13px', fontWeight:'600', color:'#374151', marginBottom:'8px' }}>รหัสผ่าน</label>
              <div style={{ position:'relative' }}>
                <span style={{ position:'absolute', left:'14px', top:'50%', transform:'translateY(-50%)', color:'#9ca3af', display:'flex' }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                  </svg>
                </span>
                <input type={showPassword ? 'text' : 'password'} placeholder="กรอกรหัสผ่าน" value={form.password}
                  onChange={e => setForm({...form, password: e.target.value})}
                  style={inputStyle({ padding:'13px 46px 13px 42px' })}
                  onFocus={e => { e.target.style.borderColor='#1565C0'; e.target.style.boxShadow='0 0 0 3px rgba(21,101,192,0.12)'; }}
                  onBlur={e => { e.target.style.borderColor='#e2e8f0'; e.target.style.boxShadow='0 1px 3px rgba(0,0,0,0.04)'; }}
                />
                <button type="button" onClick={() => setShowPassword(v => !v)} style={{
                  position:'absolute', right:'14px', top:'50%', transform:'translateY(-50%)',
                  background:'none', border:'none', cursor:'pointer', color:'#9ca3af', padding:0, display:'flex',
                }}>
                  {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              </div>
            </div>

            {/* Submit */}
            <button type="submit" disabled={loading} style={{
              width:'100%', padding:'14px',
              background: loading ? '#93c5fd' : 'linear-gradient(135deg, #1565C0, #1e88e5)',
              color:'white', border:'none', borderRadius:'12px',
              fontSize:'16px', fontWeight:'700', fontFamily:"'Sarabun', sans-serif",
              cursor: loading ? 'not-allowed' : 'pointer',
              display:'flex', alignItems:'center', justifyContent:'center', gap:'10px',
              boxShadow: loading ? 'none' : '0 4px 14px rgba(21,101,192,0.4)',
              transition:'all 0.2s',
            }}
              onMouseEnter={e => { if (!loading) e.currentTarget.style.transform='translateY(-1px)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform='translateY(0)'; }}
            >
              {loading ? (
                <>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" style={{ animation:'spin 1s linear infinite' }}>
                    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
                  </svg>
                  กำลังเข้าสู่ระบบ...
                </>
              ) : (
                <>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" y1="12" x2="3" y2="12"/>
                  </svg>
                  เข้าสู่ระบบ
                </>
              )}
            </button>
          </form>

          <div style={{ textAlign:'center', marginTop:'32px' }}>
            <p style={{ fontSize:'13px', color:'#b0bec5', marginBottom:'4px' }}>ระบบวัดผลและประเมินผลการศึกษา</p>
            <p style={{ fontSize:'12px', color:'#cfd8dc' }}>พัฒนาโดย <span style={{ fontWeight:'600', color:'#90a4ae' }}>นันทวิทย์ ชูเชื้อ</span></p>
          </div>
        </div>
      </div>

      <style>{'@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }'}</style>
    </div>
  );
}
